import {
  absBig,
  BIG_ONE,
  BIG_ZERO,
  parseBigIntToken,
} from "@/lib/tool-kit/math/bigint";
import {
  type ExprNode,
  formatExpression,
  parseExpression,
} from "@/lib/tool-kit/math/expression";
import {
  addR,
  compareR,
  divR,
  equalsR,
  fromBigInt,
  intR,
  isIntegerR,
  isZeroR,
  mulR,
  parseRational,
  RATIONAL_ZERO,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";
import {
  integerRoot,
  rationalPow,
} from "@/lib/tool-kit/number-theory/geometric-sequence";
import {
  differenceRows,
  formatPolyInN,
  newtonPolynomial,
} from "@/lib/tool-kit/number-theory/sequences";

/**
 * Evaluating Σ f(k) for k = a … b.
 *
 * The summand is parsed once and evaluated in exact rational arithmetic wherever it can
 * be (+, −, ×, ÷, whole-number powers, exact square roots, abs); anything else — sin, ln,
 * π, a fractional power — drops that term to floating point and the result is labelled
 * approximate. Before adding term by term the sum looks for a closed form:
 *
 *  - a polynomial of degree ≤ 8 in k is summed by Newton's differences, exactly and for any
 *    bounds up to 10^15 (Faulhaber's formulas are the special cases k, k², k³ …);
 *  - c·rᵏ is summed by the geometric formula, including to infinity when |r| < 1.
 */

export const MAX_DIRECT_TERMS = 10_000;
export const MAX_BOUND = BigInt(10) ** BigInt(15);
export const MAX_GEOMETRIC_BOUND = 1000;
export const MAX_POLY_DEGREE = 8;
const MAX_EXPONENT = 1000;
const MAX_VALUE_DIGITS = 5000;
const SAMPLES = 12;
export const PREVIEW_HEAD = 8;
export const PREVIEW_TAIL = 3;

export type Value = { exact: Rational } | { approx: number };

export function isExact(value: Value): value is { exact: Rational } {
  return "exact" in value;
}

export function toApprox(value: Value): number {
  return isExact(value) ? toNumberR(value.exact) : value.approx;
}

/* --------------------------------------------------------------- parse */

export type Summand =
  | { ok: true; ast: ExprNode; variable: string | null; text: string }
  | {
      ok: false;
      reason: "parse" | "many-variables";
      detail: string;
    };

export function parseSummand(raw: string): Summand {
  const parsed = parseExpression(raw);
  if (!parsed.ok) {
    return { ok: false, reason: "parse", detail: parsed.error.code };
  }
  if (parsed.variables.length > 1) {
    return {
      ok: false,
      reason: "many-variables",
      detail: parsed.variables.join(", "),
    };
  }
  return {
    ok: true,
    ast: parsed.ast,
    variable: parsed.variables[0] ?? null,
    text: formatExpression(parsed.ast),
  };
}

/* ---------------------------------------------------------- evaluation */

function approxOf(x: number): Value | null {
  return Number.isFinite(x) ? { approx: x } : null;
}

function power(base: Value, exponent: Value): Value | null {
  if (
    isExact(base) &&
    isExact(exponent) &&
    isIntegerR(exponent.exact) &&
    absBig(exponent.exact.num) <= BigInt(MAX_EXPONENT)
  ) {
    const e = Number(exponent.exact.num);
    if (isZeroR(base.exact)) {
      return e < 0 ? null : { exact: rationalPow(base.exact, e) };
    }
    const magnitude = rationalPow(base.exact, Math.abs(e));
    const digits =
      magnitude.num.toString().length + magnitude.den.toString().length;
    if (digits <= MAX_VALUE_DIGITS) {
      return { exact: e < 0 ? divR(intR(1), magnitude) : magnitude };
    }
  }
  return approxOf(toApprox(base) ** toApprox(exponent));
}

function binaryValue(op: string, left: Value, right: Value): Value | null {
  if (op === "^") {
    return power(left, right);
  }
  if (isExact(left) && isExact(right)) {
    if (op === "/") {
      return isZeroR(right.exact)
        ? null
        : { exact: divR(left.exact, right.exact) };
    }
    const combine = { "+": addR, "-": subR, "*": mulR }[op];
    return combine ? { exact: combine(left.exact, right.exact) } : null;
  }
  const a = toApprox(left);
  const b = toApprox(right);
  const results: Record<string, number> = {
    "+": a + b,
    "-": a - b,
    "*": a * b,
    "/": a / b,
  };
  return approxOf(results[op]);
}

function exactSqrt(value: Rational): Rational | null {
  if (signR(value) < 0) {
    return null;
  }
  const num = integerRoot(value.num, 2);
  const den = integerRoot(value.den, 2);
  return num * num === value.num && den * den === value.den
    ? { num, den }
    : null;
}

function callValue(fn: string, argument: Value): Value | null {
  if (isExact(argument) && fn === "abs") {
    return {
      exact:
        signR(argument.exact) < 0
          ? { num: -argument.exact.num, den: argument.exact.den }
          : argument.exact,
    };
  }
  if (isExact(argument) && fn === "sqrt") {
    const root = exactSqrt(argument.exact);
    if (root) {
      return { exact: root };
    }
  }
  const x = toApprox(argument);
  const table: Record<string, number> = {
    sqrt: Math.sqrt(x),
    sin: Math.sin(x),
    cos: Math.cos(x),
    tan: Math.tan(x),
    asin: Math.asin(x),
    acos: Math.acos(x),
    atan: Math.atan(x),
    ln: Math.log(x),
    log10: Math.log10(x),
    exp: Math.exp(x),
    abs: Math.abs(x),
  };
  return approxOf(table[fn]);
}

/** The summand at index k: exact when it can be, approximate otherwise, null if undefined. */
export function evaluateAt(
  node: ExprNode,
  variable: string | null,
  k: bigint
): Value | null {
  switch (node.kind) {
    case "number": {
      const exact = parseRational(node.text);
      return exact ? { exact } : approxOf(node.value);
    }
    case "variable":
      return node.name === variable ? { exact: fromBigInt(k) } : null;
    case "negate": {
      const inner = evaluateAt(node.operand, variable, k);
      if (inner === null) {
        return null;
      }
      return isExact(inner)
        ? { exact: { num: -inner.exact.num, den: inner.exact.den } }
        : { approx: -inner.approx };
    }
    case "binary": {
      const left = evaluateAt(node.left, variable, k);
      const right = evaluateAt(node.right, variable, k);
      return left && right ? binaryValue(node.op, left, right) : null;
    }
    default: {
      const argument = evaluateAt(node.argument, variable, k);
      return argument ? callValue(node.fn, argument) : null;
    }
  }
}

/* ------------------------------------------------------------- bounds */

export type BoundRead =
  | { kind: "ok"; value: bigint }
  | { kind: "infinity" }
  | { kind: "empty" | "invalid" | "too-large" };

const SEPARATORS = /[\s_  ]+/g;
const INFINITY_TEXT = /^[+]?(?:∞|inf|infinity|infty)$/i;

/** A whole-number bound; the upper bound may be ∞ (`allowInfinity`). */
export function readBound(raw: string, allowInfinity: boolean): BoundRead {
  const text = raw.replace(SEPARATORS, "");
  if (text === "") {
    return { kind: "empty" };
  }
  if (allowInfinity && INFINITY_TEXT.test(text)) {
    return { kind: "infinity" };
  }
  const value = parseBigIntToken(text);
  if (value === null) {
    return { kind: "invalid" };
  }
  return absBig(value) > MAX_BOUND
    ? { kind: "too-large" }
    : { kind: "ok", value };
}

/* --------------------------------------------------------- classification */

export type Shape =
  | { kind: "polynomial"; coefficients: Rational[]; degree: number }
  | { kind: "geometric"; c: Rational; r: Rational }
  | { kind: "general" };

function sampleValues(
  ast: ExprNode,
  variable: string | null
): Rational[] | null {
  const values: Rational[] = [];
  for (let k = 1; k <= SAMPLES; k += 1) {
    const value = evaluateAt(ast, variable, BigInt(k));
    if (value === null || !isExact(value)) {
      return null;
    }
    values.push(value.exact);
  }
  return values;
}

function constantRatio(values: readonly Rational[]): Rational | null {
  if (values.some(isZeroR)) {
    return null;
  }
  const first = divR(values[1], values[0]);
  for (let i = 1; i < values.length - 1; i += 1) {
    if (!equalsR(divR(values[i + 1], values[i]), first)) {
      return null;
    }
  }
  return first;
}

/** Is the summand a polynomial of degree ≤ 8 in k, or c·rᵏ? Sampled at k = 1 … 12. */
export function classifySummand(ast: ExprNode, variable: string | null): Shape {
  const samples = sampleValues(ast, variable);
  if (samples === null) {
    return { kind: "general" };
  }
  const rows = differenceRows(samples);
  // Row r is all zeros for a polynomial of degree r − 1; it keeps ≥ 2 entries up to r = 10.
  for (let r = 1; r <= MAX_POLY_DEGREE + 1; r += 1) {
    if (rows[r].every(isZeroR)) {
      const degree = Math.max(r - 1, 0);
      return {
        kind: "polynomial",
        degree,
        coefficients: newtonPolynomial(
          rows.slice(0, degree + 1).map((row) => row[0]),
          degree
        ).slice(0, degree + 1),
      };
    }
  }
  const r = constantRatio(samples);
  if (r) {
    return { kind: "geometric", r, c: divR(samples[0], r) };
  }
  return { kind: "general" };
}

/* ----------------------------------------------------------- the sum */

export type Method =
  | "empty"
  | "polynomial"
  | "geometric"
  | "infinite-geometric"
  | "direct";

export interface PreviewRow {
  k: bigint;
  term: Value | null;
}

export interface SumResult {
  method: Method;
  value: Value;
  /** Number of terms; null for an infinite series. */
  count: bigint | null;
  shape: Shape;
  /** The antiderivative F(n) = Σ_{k=1}^{n} P(k) as text, for a polynomial summand. */
  closedForm: string | null;
  /** F(b), F(a − 1) for the polynomial method. */
  endpoints: { upper: Rational; lower: Rational } | null;
  /** The geometric sum's pieces, for the geometric methods. */
  geometric: { c: Rational; r: Rational } | null;
  preview: PreviewRow[];
  /** Index in `preview` after which the table skips terms. */
  gapAfter: number | null;
  /** Some term had to be floating point. */
  approximate: boolean;
}

export interface SumFailure {
  ok: false;
  reason: "too-many-terms" | "undefined-term" | "diverges" | "needs-geometric";
}

export type SumOutcome = ({ ok: true } & SumResult) | SumFailure;

function evaluateF(coefficients: readonly Rational[], n: bigint): Rational {
  let total = RATIONAL_ZERO;
  const x = fromBigInt(n);
  for (let power = coefficients.length - 1; power >= 0; power -= 1) {
    total = addR(mulR(total, x), coefficients[power]);
  }
  return total;
}

function antiderivative(poly: readonly Rational[], degree: number): Rational[] {
  const partial: Rational[] = [];
  let running = RATIONAL_ZERO;
  for (let n = 1; n <= degree + 2; n += 1) {
    running = addR(running, evaluateF(poly, BigInt(n)));
    partial.push(running);
  }
  const rows = differenceRows(partial);
  return newtonPolynomial(
    rows.slice(0, degree + 2).map((row) => row[0]),
    degree + 1
  ).slice(0, degree + 2);
}

function previewRows(
  ast: ExprNode,
  variable: string | null,
  from: bigint,
  to: bigint
): { rows: PreviewRow[]; gapAfter: number | null } {
  const count = to - from + BIG_ONE;
  const make = (k: bigint): PreviewRow => ({
    k,
    term: evaluateAt(ast, variable, k),
  });
  if (count <= BigInt(PREVIEW_HEAD + PREVIEW_TAIL + 1)) {
    const rows: PreviewRow[] = [];
    for (let k = from; k <= to; k += BIG_ONE) {
      rows.push(make(k));
    }
    return { rows, gapAfter: null };
  }
  const rows: PreviewRow[] = [];
  for (let i = 0; i < PREVIEW_HEAD; i += 1) {
    rows.push(make(from + BigInt(i)));
  }
  for (let i = PREVIEW_TAIL - 1; i >= 0; i -= 1) {
    rows.push(make(to - BigInt(i)));
  }
  return { rows, gapAfter: PREVIEW_HEAD - 1 };
}

function directSum(
  ast: ExprNode,
  variable: string | null,
  from: bigint,
  to: bigint
): { value: Value; approximate: boolean } | null {
  let exact = RATIONAL_ZERO;
  let approx = 0;
  let compensation = 0;
  let approximate = false;
  for (let k = from; k <= to; k += BIG_ONE) {
    const term = evaluateAt(ast, variable, k);
    if (term === null) {
      return null;
    }
    if (isExact(term) && !approximate) {
      exact = addR(exact, term.exact);
      continue;
    }
    if (!approximate) {
      approximate = true;
      approx = toNumberR(exact);
    }
    // Kahan summation keeps the rounding error of a long float sum small.
    const y = toApprox(term) - compensation;
    const t = approx + y;
    compensation = t - approx - y;
    approx = t;
  }
  return {
    value: approximate ? { approx } : { exact },
    approximate,
  };
}

/** Σ_{k=from}^{to} f(k), or to ∞ when `to` is null. */
export function sumSeries(
  ast: ExprNode,
  variable: string | null,
  from: bigint,
  to: bigint | null
): SumOutcome {
  const shape = classifySummand(ast, variable);
  const base = {
    shape,
    closedForm: null,
    endpoints: null,
    geometric: null,
    approximate: false,
  };
  if (to === null) {
    return infiniteSum(ast, variable, from, shape, base);
  }
  if (to < from) {
    return {
      ok: true,
      method: "empty",
      value: { exact: RATIONAL_ZERO },
      count: BIG_ZERO,
      preview: [],
      gapAfter: null,
      ...base,
    };
  }
  const count = to - from + BIG_ONE;
  const preview = previewRows(ast, variable, from, to);
  const common = { count, preview: preview.rows, gapAfter: preview.gapAfter };
  const closed = closedFormSum(ast, variable, from, to, shape, count);
  if (closed) {
    return { ok: true, ...base, ...closed, ...common };
  }
  if (count > BigInt(MAX_DIRECT_TERMS)) {
    return { ok: false, reason: "too-many-terms" };
  }
  const direct = directSum(ast, variable, from, to);
  if (direct === null) {
    return { ok: false, reason: "undefined-term" };
  }
  return { ok: true, method: "direct", ...base, ...direct, ...common };
}

type ClosedPart = Pick<
  SumResult,
  "method" | "value" | "closedForm" | "endpoints" | "geometric"
>;

function closedFormSum(
  ast: ExprNode,
  variable: string | null,
  from: bigint,
  to: bigint,
  shape: Shape,
  count: bigint
): ClosedPart | null {
  if (shape.kind === "polynomial") {
    const lowerTerm = evaluateAt(ast, variable, from);
    const upperTerm = evaluateAt(ast, variable, to);
    const matches = (k: bigint, term: Value | null) =>
      term !== null &&
      isExact(term) &&
      equalsR(term.exact, evaluateF(shape.coefficients, k));
    if (!(matches(from, lowerTerm) && matches(to, upperTerm))) {
      return null;
    }
    const f = antiderivative(shape.coefficients, shape.degree);
    const upper = evaluateF(f, to);
    const lower = evaluateF(f, from - BIG_ONE);
    return {
      method: "polynomial",
      value: { exact: subR(upper, lower) },
      closedForm: formatPolyInN(f),
      endpoints: { upper, lower },
      geometric: null,
    };
  }
  if (
    shape.kind === "geometric" &&
    absBig(from) <= BigInt(MAX_GEOMETRIC_BOUND) &&
    absBig(to) <= BigInt(MAX_GEOMETRIC_BOUND) &&
    count <= BigInt(2 * MAX_GEOMETRIC_BOUND)
  ) {
    return {
      method: "geometric",
      value: { exact: geometricSum(shape.c, shape.r, from, count) },
      closedForm: null,
      endpoints: null,
      geometric: { c: shape.c, r: shape.r },
    };
  }
  return null;
}

function powerAt(r: Rational, exponent: bigint): Rational {
  const e = Number(exponent);
  return e >= 0 ? rationalPow(r, e) : divR(intR(1), rationalPow(r, -e));
}

/** Σ_{k=a}^{a+m−1} c·rᵏ = c·rᵃ(1 − rᵐ) ÷ (1 − r), for r ≠ 1. */
function geometricSum(
  c: Rational,
  r: Rational,
  a: bigint,
  m: bigint
): Rational {
  const lead = mulR(c, powerAt(r, a));
  return divR(mulR(lead, subR(intR(1), powerAt(r, m))), subR(intR(1), r));
}

function infiniteSum(
  ast: ExprNode,
  variable: string | null,
  from: bigint,
  shape: Shape,
  base: Pick<
    SumResult,
    "shape" | "closedForm" | "endpoints" | "geometric" | "approximate"
  >
): SumOutcome {
  if (shape.kind === "polynomial" && shape.coefficients.every(isZeroR)) {
    return {
      ok: true,
      method: "infinite-geometric",
      value: { exact: RATIONAL_ZERO },
      count: null,
      preview: [],
      gapAfter: null,
      ...base,
    };
  }
  if (
    shape.kind !== "geometric" ||
    absBig(from) > BigInt(MAX_GEOMETRIC_BOUND)
  ) {
    return {
      ok: false,
      reason: shape.kind === "polynomial" ? "diverges" : "needs-geometric",
    };
  }
  const size = compareR(
    { num: absBig(shape.r.num), den: shape.r.den },
    intR(1)
  );
  if (size >= 0) {
    return { ok: false, reason: "diverges" };
  }
  const lead = mulR(shape.c, powerAt(shape.r, from));
  const value = divR(lead, subR(intR(1), shape.r));
  const preview = previewRows(
    ast,
    variable,
    from,
    from + BigInt(PREVIEW_HEAD - 1)
  );
  return {
    ok: true,
    method: "infinite-geometric",
    value: { exact: value },
    count: null,
    preview: preview.rows,
    gapAfter: PREVIEW_HEAD - 1,
    ...base,
    geometric: { c: shape.c, r: shape.r },
  };
}
